// angular imports
import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';

// devextreme imports
import { DxPopupComponent } from 'devextreme-angular';
import { Column } from 'devextreme/ui/data_grid';
import notify from 'devextreme/ui/notify';

// cc-common imports
import { DxWidgetUtils, MessageBoxComponent, Utils } from 'cc-common';

// component imports
import { WorkOrderBaseComponent } from '../../_base/work-order-base-component';
import { WorkOrderEditorJobDetailComponent } from './work-order-editor-job-detail/work-order-editor-job-detail.component';

// service imports
import { CommunityService } from '../../../_services/data-services/community.service';

// model imports
import { WorkOrderEditorResultDto } from '../../_models/work-order-editor-result-dto';
import { WorkOrderRawDto } from '../../_models/work-order-raw-dto';
import { BaseWorkOrderParameterDto } from '../../_models/base-work-order-parameter-dto';
import { CommunitySelectorParameterDto } from '../../../_models/community-selector-parameter-dto';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { BasePropertyParametersDto } from '../../_models/base-property-parameter-dto';
import { WorkOrderResponsibleUsersResultDto } from '../../_models/work-order-responsible-users-result-dto';
import { UserInfoResultDto } from '../../../_models/user-info-result-dto';
import { AddWorkOrderResultDto } from '../../_models/add-work-order-result-dto';
import { WorkOrderLineItemRawDto } from '../../_models/work-order-line-item-raw-dto';
import { AddWorkOrderParametersDto } from '../../_models/add-work-order-parameters-dto';
import { WorkOrderResultDto } from '../../_models/work-order-result-dto';
import { EmailAttachmentResultDto } from '../../../ciramail-module/_models/email-composer/email-attachment-result-dto';
import { EmailReferenceParameterDto } from '../../../_models/email-reference-parameter-dto';

// constant imports
import { MessageInterval } from '../../../_constants/messages-intervals';
import { EmailConstants } from '../../../_constants/email-constants';

interface TabItem {
    name: string;
    text: string;
}

@Component({
    selector: 'app-work-orders-editor',
    templateUrl: './work-orders-editor.component.html',
    styleUrls: ['./work-orders-editor.component.scss']
})
export class WorkOrdersEditorComponent extends WorkOrderBaseComponent implements OnInit {
    @ViewChild('workOrderEditorPopup', { static: false }) workOrderEditorPopup: DxPopupComponent;
    @ViewChild('workOrderEditorMessageBox', { static: false }) workOrderEditorMessageBox: MessageBoxComponent;
    @ViewChild('workOrderEditorJobDetail', { static: false })
    workOrderEditorJobDetail: WorkOrderEditorJobDetailComponent;

    @Input() communityId: number = null;
    @Input() customerId: string = null;
    @Input() workOrderId: number = null;
    @Input() taskId: number = null;
    @Input() linkedEmailId: number;
    @Input() emailAttachments: Array<EmailAttachmentResultDto> = [];
    @Input() emailAccount: string;
    @Input() emailFolder: string;
    @Input() workflowId: number;
    @Input() vendorCode: string;

    @Output() onClose = new EventEmitter<boolean>();
    @Output() onManageProposalShow = new EventEmitter<WorkOrderRawDto>();
    @Output() onManageWorkAuthorizationShow = new EventEmitter<WorkOrderRawDto>();

    workOrderEditor: WorkOrderEditorResultDto;
    workOrderInfo: WorkOrderRawDto;
    workOrderTitle: string;

    visible = false;
    isWorkOrderSaved = false;

    tabItems: Array<TabItem>;
    selectedTabItem: TabItem;

    communityBoxValue: Array<number> = [];
    communityColumns: Array<Column> = [];
    communityList: Array<CommunitySelectorResultDto> = [];
    selectedCommunity: CommunitySelectorResultDto;
    isCommunityAvailable = false;

    userInfo: UserInfoResultDto;
    emailCacheId: number = null;
    emailAttachmentsFileNames: Array<string> = [];
    showEmailAttachmentSelector = false;

    constructor(protected readonly injector: Injector, private readonly communityService: CommunityService) {
        super(injector);
    }

    ngOnInit() {
        this.isCommunityAvailable = Utils.notNullAndDefined(this.communityId);
        this.userInfo = this.userService.getCurrentUserInfo();
        this.initialiseWorkOrder();
        this.getCommunityList();

        if (Utils.notNullAndDefined(this.communityId)) {
            if (Utils.notNullAndDefined(this.linkedEmailId)) {
                this.loadOpenWorkOrderItems();
            } else {
                this.configureWorkOrderEditor();
            }
        } else {
            this.configureWorkOrderEditor();
        }
    }

    show(): void {
        this.visible = true;
    }

    onShown(): void {
        this.workOrderEditorPopup.instance.repaint();
    }

    onCloseClick(): void {
        this.closeEditor();
    }

    onEditorNotesClose(forceReload: boolean): void {
        this.onNotesClose();

        if (Utils.isTrue(forceReload)) {
            this.workOrderEditor.hasNotes = true;
            this.getWorkOrderEditor();
        }
    }

    onCommunitySelection(selectedObject: any): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedRow: CommunitySelectorResultDto = selectedObject.selectedRow[0];
            this.workOrderInfo.communityId = selectedRow.communityId;
            this.selectedCommunity = selectedRow;
            this.getWorkOrderEditor();
            this.getResponsibleUsers(true);
        } else {
            this.selectedCommunity = null;
            this.configureDefaults();
        }
    }

    onSaveWorkOrder(): void {
        if (
            Utils.notNullAndDefined(this.workOrderEditorJobDetail) &&
            DxWidgetUtils.hasInstance(this.workOrderEditorJobDetail.lineItemGrid)
        ) {
            this.workOrderEditorJobDetail.lineItemGrid.instance.saveEditData().then(() => {
                this.saveWorkOrder(false);
            });
        } else {
            this.saveWorkOrder(false);
        }
    }

    onSaveAndCloseWorkOrder(): void {
        if (
            Utils.notNullAndDefined(this.workOrderEditorJobDetail) &&
            DxWidgetUtils.hasInstance(this.workOrderEditorJobDetail.lineItemGrid)
        ) {
            this.workOrderEditorJobDetail.lineItemGrid.instance.saveEditData().then(() => {
                this.saveWorkOrder(true);
            });
        } else {
            this.saveWorkOrder(true);
        }
    }

    onManageWorkAuthorizationClick(): void {
        this.onManageWorkAuthorizationShow.emit(this.workOrderInfo);
        this.closeEditor();
    }

    onManageProposalClick(): void {
        this.onManageProposalShow.emit(this.workOrderInfo);
        this.closeEditor();
    }

    onEmailAttachmentSelect(files: Array<EmailAttachmentResultDto>): void {
        if (Utils.notNullOrEmpty(files)) {
            this.emailAttachmentsFileNames = files.map((d) => d.fileName);
        }
    }

    onEmailAttachmentSelectorClose(): void {
        this.showEmailAttachmentSelector = false;
    }

    private loadOpenWorkOrderItems(): void {
        const baseParams: BasePropertyParametersDto = new BasePropertyParametersDto();
        baseParams.communityId = this.communityId;
        baseParams.clearCache = true;

        this.workOrderHelperService.showLoading();
        this.workOrderService.getOpenWorkOrders(baseParams).subscribe(
            (result: WorkOrderResultDto[]) => {
                const workOrder = result.find((d) => Utils.equals(d.customerId, this.customerId));
                if (Utils.notNullAndDefined(workOrder)) {
                    const dialog = this.helperService.getConfirmationDialog(
                        'Confirmation',
                        'There were some Open Work Orders created for this Customer. Are you sure you want to continue ?'
                    );
                    dialog.show().then((answer: boolean) => {
                        if (Utils.isTrue(answer)) {
                            this.emailCacheId = this.linkedEmailId;
                            this.configureWorkOrderEditor();
                        } else {
                            this.closeEditor();
                        }
                    });
                } else {
                    this.emailCacheId = this.linkedEmailId;
                    this.configureWorkOrderEditor();
                }
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private checkWorkOrderValidity(): any {
        const resultStatus = { isValid: true, message: null };

        if (Utils.isNullOrEmpty(this.workOrderInfo.statusCode)) {
            resultStatus.isValid = false;
            resultStatus.message = 'Status cannot be empty';
        } else if (Utils.isNullOrEmpty(this.workOrderInfo.responsibleId)) {
            resultStatus.isValid = false;
            resultStatus.message = 'Responsible cannot be empty';
        } else if (Utils.isNullOrEmpty(this.workOrderInfo.referenceTypeCode)) {
            resultStatus.isValid = false;
            resultStatus.message = 'Reference Type cannot be empty';
        } else if (Utils.isNullOrEmpty(this.workOrderInfo.priorityCode)) {
            resultStatus.isValid = false;
            resultStatus.message = 'Priority cannot be empty';
        } else if (Utils.isNullOrEmpty(this.workOrderInfo.jobTypeCode)) {
            resultStatus.isValid = false;
            resultStatus.message = 'Job Type cannot be empty';
        } else if (
            Utils.notNullOrEmpty(this.workOrderInfo.referenceTypeCode) &&
            Utils.notEqualsIgnoreCase(this.workOrderInfo.referenceTypeCode, 'O') &&
            Utils.isNullOrEmpty(this.workOrderInfo.referenceId)
        ) {
            resultStatus.isValid = false;
            resultStatus.message = 'Reference cannot be empty';
        } else if (Utils.notNullOrEmpty(this.workOrderInfo.lineItems)) {
            this.workOrderInfo.lineItems.forEach((item) => {
                if (Utils.isNullOrEmpty(item.description) || Utils.isNullOrEmpty(item.serviceCode)) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Line Item must have description and service code.';
                }
            });
        }

        return resultStatus;
    }

    private updateWorkOrder(workOrderRawInfo: WorkOrderRawDto, forceClose: boolean): void {
        this.workOrderHelperService.showLoading();

        this.workOrderService.updateWorkOrder(workOrderRawInfo).subscribe(
            () => {
                notify(
                    `Work Order ${this.workOrderInfo.workOrderId} successfully updated.`,
                    'Success',
                    MessageInterval.successMsgInterval
                );
                this.isWorkOrderSaved = true;
                this.workOrderHelperService.hideLoading();
                if (Utils.isTrue(forceClose)) {
                    this.closeEditor();
                } else {
                    this.configureWorkOrderEditor();
                }
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private saveNewWorkOrder(workOrderRawInfo: AddWorkOrderParametersDto, forceClose: boolean): void {
        this.workOrderHelperService.showLoading();

        this.workOrderService.saveWorkOrder(workOrderRawInfo).subscribe(
            (result: AddWorkOrderResultDto) => {
                this.workOrderEditorMessageBox.showSuccessMessage(
                    'Success',
                    `Work Order ${result.workOrderId} successfully added.`
                );
                this.isWorkOrderSaved = true;

                if (Utils.isTrue(forceClose)) {
                    this.workOrderHelperService.hideLoading();
                    this.closeEditor();
                } else {
                    this.communityId = result.communityId;
                    this.workOrderId = result.workOrderId;
                    this.configureWorkOrderEditor();
                }
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private saveWorkOrder(forceClose: boolean): void {
        const resultStatus = this.checkWorkOrderValidity();
        if (Utils.isTrue(resultStatus.isValid)) {
            let workOrderRawInfo: WorkOrderRawDto = new WorkOrderRawDto();
            workOrderRawInfo = Object.assign(workOrderRawInfo, this.workOrderInfo);
            workOrderRawInfo.lineItems = this.workOrderInfo.lineItems.filter((d) => Utils.notNullOrEmpty(d.crudAction));

            if (Utils.notNullAndDefined(this.workOrderInfo.workOrderId)) {
                this.updateWorkOrder(workOrderRawInfo, forceClose);
            } else {
                let addNewWorkOrderParams: AddWorkOrderParametersDto = new AddWorkOrderParametersDto();
                addNewWorkOrderParams = Object.assign(addNewWorkOrderParams, workOrderRawInfo);
                addNewWorkOrderParams.linkedTaskId = this.taskId;

                if (
                    Utils.notNullAndDefined(this.emailCacheId) &&
                    Utils.notNullOrEmpty(this.emailAccount) &&
                    Utils.notNullOrEmpty(this.emailFolder)
                ) {
                    const referenceEmail = new EmailReferenceParameterDto();
                    referenceEmail.emailCacheId = this.linkedEmailId;
                    referenceEmail.emailAccount = this.emailAccount;
                    referenceEmail.folder = this.emailFolder;

                    addNewWorkOrderParams.emailReference = referenceEmail;
                    addNewWorkOrderParams.emailAttachmentFileNames = this.emailAttachmentsFileNames;
                }

                if (Utils.notNullAndDefined(this.workflowId)) {
                    addNewWorkOrderParams.workflowId = this.workflowId;
                }

                if (Utils.notNullOrEmpty(this.vendorCode)) {
                    addNewWorkOrderParams.vendorCode = this.vendorCode;
                }

                this.saveNewWorkOrder(addNewWorkOrderParams, forceClose);
            }
        } else {
            this.workOrderEditorMessageBox.showErrorMessage('Error', resultStatus.message);
        }
    }

    private getWorkOrderEditor(): void {
        const baseParams: BaseWorkOrderParameterDto = new BaseWorkOrderParameterDto();
        baseParams.workOrderId = Utils.notNullAndDefined(this.workOrderId) ? this.workOrderId : null;
        baseParams.communityId = this.workOrderInfo.communityId;
        if (Utils.isNullOrUndefined(baseParams.communityId)) {
            return;
        }
        baseParams.customerId = this.customerId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.getWorkOrderEditor(baseParams).subscribe(
            (result: WorkOrderEditorResultDto) => {
                this.workOrderEditor = result;
                this.configureDefaults();
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private getResponsibleUsers(clearCache = false): void {
        const baseParams: BasePropertyParametersDto = new BasePropertyParametersDto();
        baseParams.communityId = this.workOrderInfo.communityId;
        baseParams.clearCache = clearCache;

        this.workOrderService.getResponsibleUsers(baseParams).subscribe(
            (result: WorkOrderResponsibleUsersResultDto[]) => {
                this.workOrderEditor.responsibleList = result;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getCommunityList(): void {
        const baseParams: CommunitySelectorParameterDto = new CommunitySelectorParameterDto();
        baseParams.includeInactive = false;
        baseParams.clearCache = true;

        this.communityService.getCommunities().subscribe(
            (result: CommunitySelectorResultDto[]) => {
                this.communityList = result;
                this.communityBoxValue = Utils.notNullAndDefined(this.communityId) ? [this.communityId] : [];
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private closeEditor(): void {
        this.visible = false;
        this.onClose.emit(this.isWorkOrderSaved);
    }

    private initialiseWorkOrder(): void {
        this.workOrderEditor = new WorkOrderEditorResultDto();
        this.workOrderInfo = new WorkOrderRawDto();

        this.tabItems = [
            {
                text: 'Job Info',
                name: 'jobInfo'
            },
            {
                text: 'Job Details',
                name: 'jobDetails'
            }
        ];
        this.selectedTabItem = this.tabItems[0];

        this.communityColumns = [
            {
                dataField: 'communityName',
                dataType: 'string',
                caption: 'Name',
                allowHeaderFiltering: false
            },
            {
                dataField: 'locationName',
                dataType: 'string',
                caption: 'Location'
            },
            {
                dataField: 'managementCompanyName',
                dataType: 'string',
                caption: 'Management Company'
            }
        ];
    }

    private configureDefaults(): void {
        this.workOrderInfo = this.workOrderEditor.workOrder;
        if (Utils.isNullOrEmpty(this.workOrderInfo.statusCode)) {
            this.workOrderInfo.statusCode = this.workOrderEditor.statusList.find((d) => Utils.isTrue(d.isDefault)).key;
        }
        if (Utils.isNullOrEmpty(this.workOrderInfo.priorityCode)) {
            this.workOrderInfo.priorityCode = this.workOrderEditor.priorityList.find((d) =>
                Utils.isTrue(d.isDefault)
            ).key;
        }
        if (
            Utils.notNullOrEmpty(this.workOrderEditor.responsibleList) &&
            Utils.isNullOrEmpty(this.workOrderInfo.responsibleId)
        ) {
            const responsibleUser = this.workOrderEditor.responsibleList.find((d) =>
                Utils.equals(d.userId, this.userInfo.userId)
            );
            if (Utils.notNullAndDefined(responsibleUser)) {
                this.workOrderInfo.responsibleId = responsibleUser.userId;
            }
        }

        if (Utils.notNullOrEmpty(this.workOrderInfo.lineItems)) {
            const lineItems: Array<WorkOrderLineItemRawDto> = [];
            this.workOrderInfo.lineItems.forEach((item, itemIndex) => {
                let itemObj: WorkOrderLineItemRawDto = new WorkOrderLineItemRawDto();
                itemObj = Object.assign(itemObj, item);

                itemObj.id = itemIndex + 1;
                lineItems.push(itemObj);
            });

            this.workOrderInfo.lineItems = lineItems;
        }

        if (Utils.notNullAndDefined(this.emailCacheId)) {
            if (Utils.isNullOrUndefined(this.workOrderId)) {
                this.showEmailAttachmentSelector = true;
            }
            if (Utils.notNullOrEmpty(this.customerId) && Utils.notEquals(this.customerId, EmailConstants.NA)) {
                this.workOrderInfo.referenceTypeCode = 'P';
            }
        }
    }

    private configureWorkOrderEditor(): void {
        this.show();
        if (Utils.notNullAndDefined(this.communityId) && Utils.notNullAndDefined(this.workOrderId)) {
            this.workOrderInfo.communityId = this.communityId;
        } else {
            this.workOrderEditor.createdBy = this.userInfo.displayName;
            this.workOrderEditor.createdDate = new Date();
        }
        this.getWorkOrderEditor();
        this.workOrderTitle = Utils.notNullOrEmpty(this.workOrderId)
            ? `View / Edit Work Order #${this.workOrderId}`
            : 'Add Work Order';
    }
}
